import {
  Component,
  effect,
  OnInit,
  Signal,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  HostListener
} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {ReceiptService} from "../../services/receipt/receipt.service";
import {Receipt, ReceiptItem, ReceiptItemProductCandidateRequest} from "../../models/receipt";
import {CommonModule, DatePipe, NgClass} from "@angular/common";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {PageHeaderService} from "../../services/page-header/page-header.service";
import {PageAction} from "../../components/app-shell/page-action.model";
import {
  ProductCandidateSelectorComponent
} from "../../components/product-candidate-selector/product-candidate-selector.component";
import {
  faBox,
  faFileImport,
  faQuestionCircle,
  faTimes,
  faUtensils,
  faDownload,
  faFilePdf,
  faList
} from "@fortawesome/free-solid-svg-icons";
import {FaIconComponent, IconDefinition} from "@fortawesome/angular-fontawesome";
import {UiButtonComponent, UiProgressComponent, UiCardComponent} from "../../shared/ui";
import {StoreSelectorComponent} from "../../components/store-selector/store-selector.component";
import {Context} from "../../models/context";
import {ContextService} from "../../services/context/context-signal.service";
import {Household} from "../../models/household";
import {Store} from "../../models/store";
import {StoreService} from "../../services/property/store.service";
import {PricePipe} from "../../pipes/price.pipe";
import {ConfirmService} from "../../services/confirm/confirm.service";
import {UiToastService} from '../../shared/ui';
import {CatalogProduct} from "../../models/catalog";
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {CatalogSearchComponent} from "../../components/catalog-search/catalog-search.component";
import {CatalogModalComponent} from "../../modals/catalog/catalog-modal.component";
import {ProductService} from "../../services/product/product.service";
import {Product} from "../../models/product";
import {Observable, of} from "rxjs";
import {catchError, distinctUntilChanged, map} from "rxjs/operators";
import {AppLogger, AppLoggerService} from '../../core/logging';

@Component({
  selector: 'app-receipt-detail',
  standalone: true,
  imports: [
    CommonModule,
    ProductCandidateSelectorComponent,
    FaIconComponent,
    StoreSelectorComponent,
    PricePipe,
    CatalogSearchComponent,
    CatalogModalComponent,
    UiProgressComponent,
    UiCardComponent,
    NgClass,
    RouterLink
  ],
  templateUrl: './receipt-detail.component.html',
  styleUrls: ['./receipt-detail.component.scss']
})
export class ReceiptDetailComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('connectorHost') connectorHost?: ElementRef<HTMLElement>;
  @ViewChild('detailAnchorTop') detailAnchorTop?: ElementRef<HTMLElement>;
  @ViewChild('detailAnchorBottom') detailAnchorBottom?: ElementRef<HTMLElement>;
  @ViewChild('masterList') masterList?: ElementRef<HTMLElement>;
  @ViewChild('catalogModal') catalogModal?: CatalogModalComponent;

  context: Signal<Context> = this.contextService.context;
  expanded: Record<number, boolean> = {};

  receiptId: number;
  receipt: Receipt = {}
  receiptItems: ReceiptItem[] = [];

  household: Household;
  stores: Store[] = [];

  // Track which receipt item is currently being searched for
  currentSearchContext: { receiptId: number, receiptItemId: number, rawProductName: string } | null = null;

  /** Search term shown in the inline catalog search on the detail panel. */
  catalogSearchTerm: string = '';

  /**
   * Forces the inline <app-catalog-search> to re-create when the term changes,
   * so its internal FormControl is reinitialized with the new searchTerm.
   */
  catalogSearchKey = 0;

  // Toggle between PDF preview and product list
  showPdfPreview = false;

  selectedItem: any | null = null;

  /** Closed SVG path for the filled ribbon connector */
  connectorPath: string | null = null;
  connectorOutlineTop: string | null = null;
  connectorOutlineBottom: string | null = null;

  private selectedRowEl: HTMLElement | null = null;
  private selectedItemId: number | null = null;

  iconFood = faUtensils
  iconNonFood = faBox
  iconUnknown = faQuestionCircle
  protected readonly faFileImport = faFileImport;
  protected readonly faTimes = faTimes;
  protected readonly faDownload = faDownload;
  protected readonly faFilePdf = faFilePdf;
  protected readonly faList = faList;

  isImporting = false;
  isDeleting = false;
  importFinishedFlash = false;

  /** Tracks whether the import modal is currently open (optional state hook). */
    // isImportModalOpen = false;
    // isDeleteModalOpen = false;

  private rafPending = false;

  /** When set, reloadReceiptItem skips its mobile scroll/highlight for this item id (used during auto-advance). */
  private suppressMobileScrollForId: number | null = null;

  /** Soft solid color used for the beam (computed from CSS). */
  beamColor = 'rgb(34, 197, 94)'; // fallback

  private readonly receiptDateFormat = 'd. MMM H:mm';

  /** True when we're in the mobile layout (below md breakpoint). */
  private isMobileViewport = typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 767.98px)').matches
    : false;

  /** Desktop: product preview for imported items (right side). */
  importedProduct$?: Observable<Product | null>;

  /** Mobile: cache imported product previews by receipt item id (so multiple collapses can show correctly). */
  importedProductByReceiptItemId: Record<number, Observable<Product | null>> = {};

  private readonly log: AppLogger;

  constructor(
    private route: ActivatedRoute,
    private receiptService: ReceiptService,
    private contextService: ContextService,
    private storeService: StoreService,
    private confirm: ConfirmService,
    private toastService: UiToastService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private pageHeaderService: PageHeaderService,
    private datePipe: DatePipe,
    private productService: ProductService,
    private appLogger: AppLoggerService,
  ) {
    this.log = this.appLogger.createForClass(ReceiptDetailComponent);
    effect(() => {
      const c = this.context();
      if (!c?.isSet?.()) return;
      this.household = c.household;
      this.loadStores(this.household.id)
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.receiptId = +params.get('id')!;
      this.loadReceipt();
      this.loadReceiptItems();
    });

    // Restore selection from URL query param on refresh/back/forward.
    this.route.queryParamMap.pipe(
      map(q => q.get('itemId')),
      distinctUntilChanged(),
    ).subscribe(raw => {
      const parsed = raw != null ? Number(raw) : NaN;
      const id = Number.isFinite(parsed) ? parsed : null;
      if (id == null) return;

      // Save desired selection id; selection will be applied when items are loaded.
      this.selectedItemId = id;

      const item = (this.receiptItems ?? []).find(it => it?.id === id);
      if (item) {
        this.selectItem(item);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      // Register declarative toolbar actions for the new AppShell.
      this.refreshPageActions();

      this.pageHeaderService.setBackButton({
        label: 'Back',
        preferHistoryBack: true,
        display: 'auto'
      });
    }, 0);

    // Ensure the connector is computed once the view is ready
    setTimeout(() => this.scheduleConnectorUpdate(), 0);

    // Compute beam color once the view is ready (CSS variables are available).
    setTimeout(() => {
      this.updateBeamColor();
      this.scheduleConnectorUpdate();
    }, 0);

    // Some browsers apply layout a tick later; do a second pass.
    setTimeout(() => this.scheduleConnectorUpdate(), 50);
  }

  /**
   * (Re)publish toolbar actions. Call whenever state that drives action
   * visibility/labels/enablement changes (receipt loaded, items reloaded,
   * showPdfPreview toggled, isImporting/isDeleting flips).
   *
   *  - Import: pinned (always visible). Disabled when nothing selected or a
   *    long-running op is in progress; shows a loading spinner during import.
   *  - Show PDF / Show List: secondary, normal action (inline on desktop,
   *    auto-collapses into ⋯ on mobile). Only shown when the receipt has a
   *    PDF file attached.
   *  - Delete: always rendered in the ⋯ overflow dropdown (mobile + desktop)
   *    via `alwaysInSubMenu: true`. Danger styling.
   */
  private refreshPageActions(): void {
    const totalSelected = this.getTotalSelected();
    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'import',
        label: 'Import',
        icon: this.faFileImport,
        variant: 'primary',
        pinned: true,
        disabled: totalSelected === 0 || this.isImporting || this.isDeleting,
        loading: this.isImporting,
        onClick: () => this.openImportModal(),
      },
    ];

    if (this.receipt?.has_file) {
      actions.push({
        type: 'button',
        id: 'toggle-pdf',
        label: this.showPdfPreview ? 'Show List' : 'Show PDF',
        icon: this.showPdfPreview ? this.faList : this.faFilePdf,
        variant: 'secondary',
        onClick: () => this.togglePdfPreview(),
      });
    }

    actions.push({
      type: 'button',
      id: 'delete',
      label: 'Delete',
      icon: this.faTimes,
      variant: 'danger',
      alwaysInSubMenu: true,
      disabled: this.isDeleting || this.isImporting,
      loading: this.isDeleting,
      onClick: () => this.openDeleteModal(),
    });

    this.pageHeaderService.setPageActions(actions);
  }

  onMasterListScroll() {
    this.scheduleConnectorUpdate();
  }

  private scheduleConnectorUpdate() {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.updateConnector();
    });
  }

  ngOnDestroy(): void {
    // Clear toolbar actions when navigating away
    this.pageHeaderService.clearAll();
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
      this.highlightTimeoutId = null;
    }
  }

  private updateConnector() {
    if (window.innerWidth < 768) {
      this.connectorPath = null;
      this.connectorOutlineTop = null;
      this.connectorOutlineBottom = null;
      return;
    }

    const host = this.connectorHost?.nativeElement;
    const topEl = this.detailAnchorTop?.nativeElement;
    const bottomEl = this.detailAnchorBottom?.nativeElement;

    if (!host || !this.selectedRowEl || !topEl || !bottomEl) {
      this.connectorPath = null;
      this.connectorOutlineTop = null;
      this.connectorOutlineBottom = null;
      return;
    }

    const hostRect = host.getBoundingClientRect();
    const startRect = this.selectedRowEl.getBoundingClientRect();
    const topRect = topEl.getBoundingClientRect();
    const bottomRect = bottomEl.getBoundingClientRect();

    const sx = startRect.left + startRect.width / 2 - hostRect.left;
    const sy = startRect.top + startRect.height / 2 - hostRect.top;

    const tx = topRect.left + topRect.width / 2 - hostRect.left;
    const ty = topRect.top + topRect.height / 2 - hostRect.top;

    const bx = bottomRect.left + bottomRect.width / 2 - hostRect.left;
    const by = bottomRect.top + bottomRect.height / 2 - hostRect.top;

    // Shape tuning: more “ribbon”, less “triangle”.
    // Use slightly different control points for top/bottom to add a subtle twist.
    const dist = Math.max(180, Math.abs(tx - sx));
    const dx1 = dist * 0.42;
    const dx2 = dist * 0.55;

    const topC1x = sx + dx2;
    const topC1y = sy - 8;
    const topC2x = tx - dx1;
    const topC2y = ty;

    const botC1x = sx + dx2;
    const botC1y = sy + 8;
    const botC2x = bx - dx1;
    const botC2y = by;

    this.connectorOutlineTop = `M ${sx.toFixed(1)} ${sy.toFixed(1)} C ${topC1x.toFixed(1)} ${topC1y.toFixed(1)}, ${topC2x.toFixed(1)} ${topC2y.toFixed(1)}, ${tx.toFixed(1)} ${ty.toFixed(1)}`;
    this.connectorOutlineBottom = `M ${sx.toFixed(1)} ${sy.toFixed(1)} C ${botC1x.toFixed(1)} ${botC1y.toFixed(1)}, ${botC2x.toFixed(1)} ${botC2y.toFixed(1)}, ${bx.toFixed(1)} ${by.toFixed(1)}`;

    // Closed area: top curve -> detail top anchor -> straight down to detail bottom anchor ->
    // bottom curve reversed back to the start (swap control points).
    this.connectorPath = [
      `M ${sx.toFixed(1)} ${sy.toFixed(1)}`,
      `C ${topC1x.toFixed(1)} ${topC1y.toFixed(1)}, ${topC2x.toFixed(1)} ${topC2y.toFixed(1)}, ${tx.toFixed(1)} ${ty.toFixed(1)}`,
      `L ${bx.toFixed(1)} ${by.toFixed(1)}`,
      // reverse(bottom): end -> C (c2) (c1) start
      `C ${botC2x.toFixed(1)} ${botC2y.toFixed(1)}, ${botC1x.toFixed(1)} ${botC1y.toFixed(1)}, ${sx.toFixed(1)} ${sy.toFixed(1)}`,
      'Z'
    ].join(' ');
  }

  loadStores(householdId: number): void {
    this.storeService.stores(householdId).subscribe((data: Store[]) => {
      this.stores = data;
    });
  }

  private updateHeaderTitleFromReceipt(): void {
    const raw = this.receipt?.purchase_date;
    if (!raw) {
      this.pageHeaderService.setPageTitle('Receipt');
      return;
    }

    // Keep consistent with the template formatting (uses UTC).
    const formatted = this.datePipe.transform(raw, this.receiptDateFormat, 'UTC') ?? 'Receipt';
    this.pageHeaderService.setPageTitle(formatted);
  }

  loadReceipt() {
    this.receiptService.receipt(this.receiptId).subscribe(receipt => {
      this.receipt = receipt;
      this.updateHeaderTitleFromReceipt();
      // `has_file` may have just become known → publish "Show PDF" action.
      this.refreshPageActions();
    })
  }

  private updateSelectedItemQueryParam(receiptItemId: number | null): void {
    if (receiptItemId == null) return;

    // Don't pollute the flow on mobile.
    if (this.isMobileViewport) return;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {itemId: receiptItemId},
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private loadImportedProductPreviewForSelectedItem(): void {
    // Desktop only.
    if (this.isMobileViewport) {
      this.importedProduct$ = undefined;
      return;
    }

    const importedId = this.selectedItem?.imported_product_id;
    if (!importedId) {
      this.importedProduct$ = of(null);
      return;
    }

    this.importedProduct$ = this.productService.getProduct(importedId).pipe(
      catchError(() => of(null))
    );
  }

  loadReceiptItems() {
    this.receiptService.receiptItems(this.receiptId).subscribe(receiptItems => {
      this.receiptItems = receiptItems;
      // Expand all non-imported items by default
      this.receiptItems.forEach(item => {
        if (!item.is_imported && this.expanded[item.id] === undefined) {
          this.expanded[item.id] = true;
        }
      });

      // Import button enable state depends on the selection count.
      this.refreshPageActions();

      // If URL had an itemId, apply it now.
      if (!this.isMobileViewport && this.selectedItemId != null) {
        const item = (this.receiptItems ?? []).find(it => it?.id === this.selectedItemId);
        if (item) {
          this.selectItem(item);
          return;
        }
      }

      // Desktop: auto-select the first unmapped item on initial load.
      setTimeout(() => this.autoSelectFirstUnmappedOnDesktop(), 0);
    })
  }

  /**
   * After list updates/re-sorts, the old DOM element references may become stale.
   * Re-find the current row anchor for the selected item and update the connector.
   */
  private rebindSelectedRowAnchor(): void {
    if (this.selectedItemId == null) {
      this.selectedRowEl = null;
      return;
    }

    const host = this.connectorHost?.nativeElement;
    if (!host) return;

    const el = host.querySelector<HTMLElement>(`[data-receipt-item-id="${this.selectedItemId}"] [data-beam-anchor="true"]`);
    if (el) {
      this.selectedRowEl = el;
    }
  }

  /** Smoothly scroll the master list so the selected row stays in view after resort/reload. */
  private scrollSelectedMasterRowIntoView(): void {
    if (this.selectedItemId == null) return;
    const list = this.masterList?.nativeElement;
    if (!list) return;

    const row = list.querySelector<HTMLElement>(`[data-receipt-item-id="${this.selectedItemId}"]`);
    if (!row) return;

    // Only scroll if it's not fully visible to avoid annoying micro-scrolls.
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const padding = 12;

    const isAbove = rowRect.top < listRect.top + padding;
    const isBelow = rowRect.bottom > listRect.bottom - padding;
    if (!isAbove && !isBelow) return;

    row.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'nearest'});
  }

  reloadReceiptItem(receiptItemId: number) {
    this.receiptService.receiptItem(this.receiptId, receiptItemId).subscribe(receiptItem => {
      const index = this.receiptItems.findIndex(item => item.id === receiptItemId);
      if (index !== -1) {
        this.receiptItems[index] = receiptItem;
        // Selection state may have changed → Import disable flag may change.
        this.refreshPageActions();
        // Ensure non-imported items remain expanded
        if (!receiptItem.is_imported && this.expanded[receiptItem.id] === undefined) {
          this.expanded[receiptItem.id] = true;
        }

        // If the currently selected item was updated, refresh the detail binding.
        if (this.selectedItem?.id === receiptItemId) {
          this.selectedItem = receiptItem;
        }

        // Detail height/content can change (candidate selector, badges, etc.)
        // so redraw the connector after the DOM settles.
        setTimeout(() => {
          this.rebindSelectedRowAnchor();
          this.scrollSelectedMasterRowIntoView();
          this.scheduleConnectorUpdate();
        }, 0);
        setTimeout(() => {
          this.rebindSelectedRowAnchor();
          this.scrollSelectedMasterRowIntoView();
          this.scheduleConnectorUpdate();
        }, 80);
      }

      // Mobile: scroll/highlight the item after resorting — skipped when auto-advancing to the next item.
      const suppressScroll = this.suppressMobileScrollForId === receiptItemId;
      if (suppressScroll) this.suppressMobileScrollForId = null;

      if (!suppressScroll) {
        setTimeout(() => this.scrollMobileReceiptItemIntoView(receiptItemId, 'smooth'), 0);
        setTimeout(() => {
          this.scrollMobileReceiptItemIntoView(receiptItemId, 'smooth');
          this.flashHighlightReceiptItem(receiptItemId);
        }, 160);
      }
    });
  }

  productCandidateSelectionChanged(item: ReceiptItem) {
    const wasUnmatched = item.selected_product_candidate_id == null && !item.is_imported;
    const nextItem = wasUnmatched ? this.findNextUnmatchedItem(item.id) : null;
    const willAdvanceMobile = wasUnmatched && nextItem !== null && this.isMobileViewport;

    if (willAdvanceMobile) {
      this.suppressMobileScrollForId = item.id;
    }

    this.reloadReceiptItem(item.id);

    // Mobile: scroll to the just-matched item only when not advancing to a next item.
    if (!willAdvanceMobile) {
      setTimeout(() => this.scrollMobileReceiptItemIntoView(item.id, 'smooth'), 0);
      setTimeout(() => {
        this.scrollMobileReceiptItemIntoView(item.id, 'smooth');
        this.flashHighlightReceiptItem(item.id);
      }, 200);
    }

    if (wasUnmatched) {
      this.advanceAfterMatch(nextItem);
    }
  }

  private openMobileCatalogModal(context: { receiptId: number, receiptItemId: number, rawProductName: string }): void {
    this.currentSearchContext = context;
    this.catalogSearchTerm = context.rawProductName;
    this.catalogSearchKey++;

    // Ensure the modal is present before opening.
    setTimeout(() => {
      this.catalogModal?.open();
    }, 0);
  }

  onSearchRequested(context: { receiptId: number, receiptItemId: number, rawProductName: string }) {
    // Mobile: show modal.
    if (this.isMobileViewport) {
      this.openMobileCatalogModal(context);
      return;
    }

    // Desktop: inline panel.
    this.currentSearchContext = context;
    this.catalogSearchTerm = context.rawProductName;
    this.catalogSearchKey++;
  }

  // Called when the user selects a product from the mobile modal.
  onMobileCatalogItemSelected(selectedItem: CatalogProduct) {
    this.onCatalogItemSelected(selectedItem);
  }

  onCatalogItemSelected(selectedItem: CatalogProduct) {
    if (!this.currentSearchContext) return;

    const receiptItemId = this.currentSearchContext.receiptItemId;

    // Capture advance target before the async API call changes any state.
    const matchedItem = this.receiptItems.find(it => it.id === receiptItemId);
    const wasUnmatched = matchedItem != null
      && matchedItem.selected_product_candidate_id == null
      && !matchedItem.is_imported;
    const nextItem = wasUnmatched ? this.findNextUnmatchedItem(receiptItemId) : null;
    const willAdvanceMobile = wasUnmatched && nextItem !== null && this.isMobileViewport;

    const newCandidate: ReceiptItemProductCandidateRequest = {
      catalog_id: selectedItem.id,
      product_name: selectedItem.name,
      product_brand: selectedItem.brand,
      source_provider: selectedItem.provider,
      provider_reference: selectedItem.provider_reference,
      provider_product_url: selectedItem.provider_product_url,
      thumb_image_url: selectedItem.image_thumb_url,
      is_selected: true,
    };

    this.receiptService.createProductCandidate(
      this.currentSearchContext.receiptId,
      receiptItemId,
      newCandidate
    ).subscribe({
      next: () => {
        const refreshedId = receiptItemId;
        this.currentSearchContext = null;

        if (willAdvanceMobile) {
          this.suppressMobileScrollForId = refreshedId;
        }

        this.reloadReceiptItem(refreshedId);

        // Mobile: scroll/highlight to wherever the item ended up — skipped when auto-advancing.
        if (!willAdvanceMobile) {
          setTimeout(() => this.scrollMobileReceiptItemIntoView(refreshedId, 'smooth'), 0);
          setTimeout(() => {
            this.scrollMobileReceiptItemIntoView(refreshedId, 'smooth');
            this.flashHighlightReceiptItem(refreshedId);
          }, 260);
        }

        if (wasUnmatched) {
          this.advanceAfterMatch(nextItem);
        }
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === HttpStatusCode.Conflict) {
          this.toastService.error(`"${newCandidate.product_name}" is already a candidate for "${this.currentSearchContext!.rawProductName}".`);
          return;
        }
        this.toastService.error(`An error occurred while creating the product candidate: ${err.message}`);
      }
    });
  }

  onInlineCatalogItemSelected(selectedItem: CatalogProduct) {
    // Use the same creation pathway.
    this.onCatalogItemSelected(selectedItem);
  }

  onStoreSelectionChanged(item: ReceiptItem, store: Store) {
    this.receiptService.selectTargetStoreId(this.receiptId, item.id, store.id).subscribe(() => {
      this.reloadReceiptItem(item.id)
    });
  }

  openDeleteModal(): void {
    if (!this.receiptId || this.isDeleting || this.isImporting) return;

    // Use app-wide ConfirmService instead of opening a local modal.
    this.confirm.ask({
      title: 'Delete receipt',
      message: `Do you really want to delete this receipt? This action can't be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-danger',
      modalOptions: {size: 'sm'}
    }).then(ok => {
      if (!ok) return;
      this.confirmDeleteReceipt();
    });
  }

  onDeleteModalClosed(): void {
    // no-op (kept for template compatibility; local modal removed)
  }

  openImportModal(): void {
    const totalSelected = this.getTotalSelected();
    if (totalSelected === 0 || this.isImporting || this.isDeleting) return;

    // Use app-wide ConfirmService instead of opening a local modal.
    this.confirm.ask({
      title: 'Import to stock',
      message: `Import ${totalSelected} selected product(s) to stock?`,
      confirmText: 'Import',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-success',
      modalOptions: {size: 'sm'}
    }).then(ok => {
      if (!ok) return;
      this.confirmImportSelectedProducts();
    });
  }

  onImportModalClosed(): void {
    // no-op (kept for template compatibility; local modal removed)
  }

  confirmDeleteReceipt(): void {
    if (!this.receiptId || this.isDeleting || this.isImporting) {
      return;
    }

    this.isDeleting = true;
    this.refreshPageActions();

    this.receiptService.deleteReceipt(this.receiptId).subscribe({
      next: () => {
        this.toastService.success('Receipt deleted.');
        this.router.navigate(['/receipts']);
      },
      error: (err) => {
        this.log.error('Error deleting receipt', err, {receiptId: this.receiptId});
        this.toastService.error('Failed to delete receipt.');
        this.isDeleting = false;
        this.refreshPageActions();
      }
    });
  }

  confirmImportSelectedProducts(): void {
    const totalSelected = this.getTotalSelected();
    if (totalSelected === 0 || this.isImporting || this.isDeleting) {
      return;
    }

    this.isImporting = true;
    this.refreshPageActions();

    this.receiptService.importReceiptToStock(this.receiptId).subscribe({
      next: () => {
        // Refresh UI
        this.loadReceiptItems();
        this.loadReceipt();

        this.toastService.success('Products imported successfully!');

        this.importFinishedFlash = true;
        setTimeout(() => {
          this.importFinishedFlash = false;
        }, 2500);

        this.isImporting = false;
        this.refreshPageActions();
      },
      error: (error) => {
        this.log.error('Error importing products from receipt', error, {receiptId: this.receiptId});
        this.toastService.error('Failed to import products. Please try again later.');
        this.isImporting = false;
        this.refreshPageActions();
      }
    });
  }

  public getTypeIcon(type: string): IconDefinition {
    switch (type?.toLowerCase()) {
      case 'food':
        return this.iconFood; // or return a class like 'fa fa-apple' if you're using FontAwesome
      case 'nonfood':
        return this.iconNonFood;
      default:
        return this.iconUnknown;
    }
  }

  getTypeColor(type: string): string {
    switch (type?.toLowerCase()) {
      case 'food':
        return 'green';
      case 'nonfood':
        return 'brown';
      default:
        return 'gray';
    }
  }


  getTotalSelected(): number {

    let totalSelected = 0;

    for (const item of this.receiptItems) {
      if (item.selected_product_candidate_id && !item.is_imported) {
        totalSelected = totalSelected + 1;
      }
    }

    return totalSelected;
  }

  getTotalUnimported(): number {
    let totalUnimported = 0;

    for (const item of this.receiptItems) {
      if (!item.is_imported) {
        totalUnimported = totalUnimported + 1;
      }
    }

    return totalUnimported;
  }

  private ensureMobileImportedProductPreviewLoaded(item: ReceiptItem): void {
    if (!this.isMobileViewport) return;
    const receiptItemId = item?.id;
    const importedProductId = item?.imported_product_id;
    if (!receiptItemId || !importedProductId) return;

    if (this.importedProductByReceiptItemId[receiptItemId]) return;

    this.importedProductByReceiptItemId[receiptItemId] = this.productService.getProduct(importedProductId).pipe(
      catchError(() => of(null))
    );
  }

  setExpanded(item: any, isExpanded: boolean) {
    this.expanded[item.id] = isExpanded;

    // Mobile: if an imported item is opened, load its product preview for display in the collapse.
    if (isExpanded && item?.is_imported) {
      this.ensureMobileImportedProductPreviewLoaded(item);
    }
  }

  isExpanded(item: any): boolean {
    return !!this.expanded[item.id];
  }

  getPdfUrl(): SafeResourceUrl {
    if (!this.receipt?.file_url) {
      return '';
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.receipt.file_url);
  }

  togglePdfPreview() {
    this.showPdfPreview = !this.showPdfPreview;
    // Toggle label/icon of the "Show PDF / Show List" action.
    this.refreshPageActions();
  }

  selectItem(item: any, rowEl?: HTMLElement) {
    this.selectedItem = item;
    this.selectedItemId = item?.id ?? null;
    if (rowEl) this.selectedRowEl = rowEl;
    this.setExpanded(item, true);

    // Reset inline search when changing the selected master item
    this.currentSearchContext = null;
    this.catalogSearchTerm = '';
    this.catalogSearchKey++;

    // Auto-open inline search if item is unmapped and has zero candidates
    const isUnmapped = item?.selected_product_candidate_id == null;
    const candidatesCount = Array.isArray(item?.product_candidates) ? item.product_candidates.length : 0;
    if (isUnmapped && candidatesCount === 0 && !item?.is_imported) {
      this.currentSearchContext = {
        receiptId: item.receipt_id,
        receiptItemId: item.id,
        rawProductName: item.name
      };
      this.catalogSearchTerm = item.name ?? '';
      this.catalogSearchKey++;
    }

    // Persist selection in URL (desktop only) so it survives refresh.
    this.updateSelectedItemQueryParam(this.selectedItemId);

    // Update imported-product preview card (desktop only).
    this.loadImportedProductPreviewForSelectedItem();

    // Let the DOM settle before measuring
    setTimeout(() => {
      this.rebindSelectedRowAnchor();
      this.scrollSelectedMasterRowIntoView();
      this.scheduleConnectorUpdate();
    }, 0);
  }

  private updateBeamColor() {
    try {
      const probe = document.createElement('div');
      probe.className = 'bg-primary';
      probe.style.position = 'absolute';
      probe.style.left = '-9999px';
      probe.style.top = '-9999px';
      document.body.appendChild(probe);

      const bg = getComputedStyle(probe).backgroundColor;
      document.body.removeChild(probe);

      if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
        this.beamColor = bg;
      }
    } catch {
      // keep fallback
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobileViewport = window.matchMedia('(max-width: 767.98px)').matches;

    this.updateBeamColor();
    this.scheduleConnectorUpdate();

    // After breakpoint/layout changes, do an extra pass once reflow is done.
    setTimeout(() => this.scheduleConnectorUpdate(), 80);
  }

  @HostListener('window:orientationchange')
  onOrientationChange() {
    this.onWindowResize();
  }

  /**
   * Sorted view for the master list:
   * - Unmapped (no selected_product_candidate_id) first
   * - Mapped afterwards
   * Stable within each group by original index.
   */
  get sortedReceiptItems(): ReceiptItem[] {
    const items = this.receiptItems ?? [];

    // decorate with original index for stability
    return items
      .map((it, idx) => ({it, idx}))
      .sort((a, b) => {
        const aMapped = a.it.selected_product_candidate_id != null;
        const bMapped = b.it.selected_product_candidate_id != null;

        if (aMapped !== bMapped) return aMapped ? 1 : -1;
        return a.idx - b.idx;
      })
      .map(x => x.it);
  }

  /** Mobile: briefly highlight an item after mapping changes and auto-scroll. */
  highlightedReceiptItemId: number | null = null;
  private highlightTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private flashHighlightReceiptItem(receiptItemId: number, durationMs = 2000): void {
    if (!this.isMobileViewport) return;
    if (!receiptItemId) return;

    this.highlightedReceiptItemId = receiptItemId;
    if (this.highlightTimeoutId) {
      clearTimeout(this.highlightTimeoutId);
    }
    this.highlightTimeoutId = setTimeout(() => {
      if (this.highlightedReceiptItemId === receiptItemId) {
        this.highlightedReceiptItemId = null;
      }
      this.highlightTimeoutId = null;
    }, durationMs);
  }

  private scrollMobileReceiptItemIntoView(receiptItemId: number, behavior: ScrollBehavior = 'smooth'): void {
    if (!this.isMobileViewport) return;
    if (!receiptItemId) return;

    const el = document.querySelector<HTMLElement>(`[data-receipt-item-id="${receiptItemId}"]`);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const fullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
    if (fullyVisible) return;

    try {
      el.scrollIntoView({behavior, block: 'center', inline: 'nearest'});
    } catch {
      el.scrollIntoView();
    }
  }

  private autoSelectFirstUnmappedOnDesktop(): void {
    if (this.isMobileViewport) return;
    if (this.selectedItemId != null) return;

    const firstUnmapped = (this.sortedReceiptItems ?? []).find(it =>
      !it?.is_imported && it?.selected_product_candidate_id == null
    );

    if (firstUnmapped) {
      this.selectItem(firstUnmapped);
    }

    // If there is no unmapped item, optionally select the first item.
    if (!firstUnmapped) {
      const first = (this.sortedReceiptItems ?? []).find(it => !it?.is_imported);
      if (first) this.selectItem(first);
    }
  }

  /**
   * Returns the first unmatched item (no selected_product_candidate_id, not imported)
   * that follows `afterItemId` in the current sorted order. Falls back to the first
   * remaining unmatched item anywhere in the list. Returns null when none remain.
   */
  private findNextUnmatchedItem(afterItemId: number): ReceiptItem | null {
    const sorted = this.sortedReceiptItems;
    const currentIndex = sorted.findIndex(it => it.id === afterItemId);
    const isUnmatched = (it: ReceiptItem) =>
      it.selected_product_candidate_id == null && !it.is_imported;

    if (currentIndex !== -1) {
      const next = sorted.slice(currentIndex + 1).find(isUnmatched);
      if (next) return next;
    }
    return sorted.find(it => it.id !== afterItemId && isUnmatched(it)) ?? null;
  }

  /**
   * After an unmatched → matched transition, advance the UI to `nextItem`.
   * Desktop: switches the detail pane (or clears it when all matched).
   * Mobile: expands the collapse, scrolls, and highlights the next item.
   */
  private advanceAfterMatch(nextItem: ReceiptItem | null): void {
    if (!this.isMobileViewport) {
      setTimeout(() => {
        if (nextItem) {
          this.selectItem(nextItem);
        } else {
          this.selectedItem = null;
          this.selectedItemId = null;
          this.selectedRowEl = null;
          this.connectorPath = null;
        }
      }, 0);
    } else {
      if (!nextItem) return;
      setTimeout(() => {
        this.setExpanded(nextItem, true);
        this.scrollMobileReceiptItemIntoView(nextItem.id, 'smooth');
        this.flashHighlightReceiptItem(nextItem.id);
      }, 150);
    }
  }

  /**
   * Mobile: called when the catalog modal closes (X/backdrop/ESC) without necessarily selecting an item.
   * Ensures the originating receipt item doesn't get stuck in "search open" state.
   */
  onMobileCatalogModalClosed(): void {
    this.currentSearchContext = null;
  }

}
